import type {
  TeacherExamItem,
  WorksheetProgressQuestionItem,
  WorksheetProgressRequest,
  WorksheetProgressResponse,
  WorksheetProgressRow,
} from '../services/api';
import type { StudentEngagementSummary } from '../types';

/**
 * Sample worksheet progress for the internal testing account(s) only.
 * Every other account always gets real data from the API.
 */
const SAMPLE_DATA_ACCOUNTS = ['karun@hps'];

export const usesSampleWorksheetData = (username?: string | null) =>
  !!username && SAMPLE_DATA_ACCOUNTS.includes(username.trim().toLowerCase());

const DAY_MS = 24 * 60 * 60 * 1000;
const SAMPLE_STUDENT_COUNT = 4;
const WORKSHEETS_PER_STUDENT = 3;
const TOPICS = ['Linear Equations', 'Polynomials', 'Coordinate Geometry'];

/**
 * Per-student profile.
 * - weekly: questions solved in each of the last 6 weeks (oldest → this week)
 * - worksheets: questions in each worksheet, how many are solved / attempted.
 *   Solves are worked through in order — first worksheet in the early weeks,
 *   then the second, then the third — so each worksheet moves in its own weeks
 *   while the overall completion keeps rising.
 *   sum(weekly) === sum(worksheets[].solved)
 */
const PROFILES = [
  { weekly: [2, 3, 3, 4, 4, 5], worksheets: [{ total: 9, solved: 9, attempted: 0 }, { total: 8, solved: 7, attempted: 1 }, { total: 8, solved: 5, attempted: 1 }] }, // 21/25
  { weekly: [2, 2, 3, 3, 4, 4], worksheets: [{ total: 8, solved: 8, attempted: 0 }, { total: 8, solved: 6, attempted: 1 }, { total: 8, solved: 4, attempted: 2 }] }, // 18/24
  { weekly: [1, 2, 3, 3, 3, 4], worksheets: [{ total: 8, solved: 7, attempted: 1 }, { total: 7, solved: 5, attempted: 1 }, { total: 7, solved: 4, attempted: 1 }] }, // 16/22
  { weekly: [1, 2, 2, 2, 3, 3], worksheets: [{ total: 8, solved: 6, attempted: 1 }, { total: 7, solved: 4, attempted: 1 }, { total: 7, solved: 3, attempted: 2 }] }, // 13/22
];

// The worksheets were handed out about 6 weeks ago, a day apart.
const ASSIGNED_DAYS_AGO = [42, 41, 40];

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 30).getTime();
};
const daysAgo = (days: number) => new Date(startOfToday() - days * DAY_MS).toISOString();

/**
 * Days-ago for every solve, oldest first. Week k (0 = six weeks ago, 5 = this week)
 * covers days (35 - 7k) … (41 - 7k); its solves are spread over different days.
 */
const solveSchedule = (weekly: number[]) => weekly.flatMap((count, k) => {
  const mostRecentDay = 35 - 7 * k;
  return Array.from({ length: count }, (_, j) => Math.min(39, mostRecentDay + Math.floor((j * 7) / count)))
    .sort((a, b) => b - a);
});

const examTime = (e: TeacherExamItem) => e.processed_at ?? e.created_at ?? '';

/** The exams a student's worksheets come from (always includes the selected exam), oldest first. */
const worksheetExams = (exams: TeacherExamItem[], selectedExamId?: number) => {
  const newestFirst = [...exams].sort((a, b) => examTime(b).localeCompare(examTime(a)));
  let picked = newestFirst.slice(0, WORKSHEETS_PER_STUDENT);
  if (selectedExamId != null && !picked.some((e) => e.exam_id === selectedExamId)) {
    const selected = exams.find((e) => e.exam_id === selectedExamId);
    if (selected) picked = [...picked.slice(0, WORKSHEETS_PER_STUDENT - 1), selected];
  }
  return picked.sort((a, b) => examTime(a).localeCompare(examTime(b)));
};

/** All worksheet rows for one student — one row per worksheet (exam). */
const buildStudentRows = (
  student: StudentEngagementSummary,
  exams: TeacherExamItem[],
  profileIndex: number,
): WorksheetProgressRow[] => {
  if (exams.length === 0) return [];
  const profile = PROFILES[profileIndex % PROFILES.length];

  // Fewer exams than worksheets: fold the extra worksheets into the last one.
  const sheets = profile.worksheets.slice(0, exams.length).map((w) => ({ ...w }));
  profile.worksheets.slice(exams.length).forEach((w) => {
    const last = sheets[sheets.length - 1];
    last.total += w.total;
    last.solved += w.solved;
    last.attempted += w.attempted;
  });

  const schedule = solveSchedule(profile.weekly);
  let cursor = 0;

  return exams.map((exam, w) => {
    const sheet = sheets[w];
    const solveDays = schedule.slice(cursor, cursor + sheet.solved);
    cursor += sheet.solved;
    const assignedDaysAgo = ASSIGNED_DAYS_AGO[Math.min(w, ASSIGNED_DAYS_AGO.length - 1)];

    const progress: WorksheetProgressQuestionItem[] = Array.from({ length: sheet.total }, (_, q) => {
      const status: WorksheetProgressQuestionItem['status'] = q < sheet.solved ? 'solved' : q < sheet.solved + sheet.attempted ? 'attempted' : 'assigned';
      const submittedDaysAgo = status === 'solved' ? solveDays[q] : status === 'attempted' ? 1 + ((q + w) % 4) : null;
      return {
        question_id: q + 1,
        topic_name: TOPICS[(q + w) % TOPICS.length],
        status,
        answer_type: 'text',
        attempt_count: status === 'assigned' ? 0 : status === 'solved' ? 1 + (q % 2) : 1,
        obtained_marks: status === 'solved' ? 2 : status === 'attempted' ? 1 : null,
        total_marks: 2,
        question_marks: 2,
        last_submitted_at: submittedDaysAgo == null ? null : daysAgo(submittedDaysAgo),
        created_at: daysAgo(assignedDaysAgo),
        updated_at: daysAgo(submittedDaysAgo ?? assignedDaysAgo),
      };
    });

    const submitted = progress.map((p) => p.last_submitted_at).filter((d): d is string => !!d).sort();
    return {
      student_id: student.student_id,
      student_name: (student.full_name || student.username || 'Student').toUpperCase(),
      username: student.username ?? null,
      student_result_id: exam.exam_id * 1000 + student.student_id,
      exam: {
        id: exam.exam_id,
        name: exam.name || exam.exam_name || `Exam #${exam.exam_id}`,
        subject: exam.subject ?? null,
        exam_date: (exam.processed_at || exam.created_at || daysAgo(assignedDaysAgo + 1)).slice(0, 10),
      },
      worksheet_opened: true,
      summary: {
        total: sheet.total,
        assigned: sheet.total - sheet.solved - sheet.attempted,
        attempted: sheet.attempted,
        solved: sheet.solved,
      },
      solved_percent: Math.round((sheet.solved / sheet.total) * 100),
      last_submitted_at: submitted.length ? submitted[submitted.length - 1] : null,
      progress,
    };
  });
};

export interface SampleWorksheetData {
  response: WorksheetProgressResponse;
  /** Every worksheet row per student (used by the student's progress chart). */
  rowsByStudent: Map<number, WorksheetProgressRow[]>;
}

/**
 * Builds the worksheet-progress view for the sample account.
 * `candidateStudents` must already be filtered to the selected class/section (class mode).
 */
export const buildSampleWorksheetData = (
  params: WorksheetProgressRequest,
  exams: TeacherExamItem[],
  candidateStudents: StudentEngagementSummary[],
): SampleWorksheetData => {
  const pool = candidateStudents.slice(0, SAMPLE_STUDENT_COUNT);
  if (params.student_id && !pool.some((s) => s.student_id === params.student_id)) {
    const picked = candidateStudents.find((s) => s.student_id === params.student_id);
    if (picked) pool.push(picked);
  }
  const sheetExams = worksheetExams(exams, params.exam_id);

  const rowsByStudent = new Map<number, WorksheetProgressRow[]>();
  pool.forEach((student, i) => rowsByStudent.set(student.student_id, buildStudentRows(student, sheetExams, i)));

  const rows = Array.from(rowsByStudent.values())
    .flat()
    .filter((row) => (params.exam_id ? row.exam.id === params.exam_id : true))
    .filter((row) => (params.student_id ? row.student_id === params.student_id : true));

  return {
    response: {
      scope: {
        type: params.exam_id ? 'exam' : 'class',
        exam_id: params.exam_id ?? null,
        class_name: params.class_name ?? null,
        section: params.section ?? null,
        subject: params.subject ?? null,
        student_id: params.student_id ?? null,
      },
      total_rows: rows.length,
      page: 1,
      page_size: rows.length || 1,
      rows,
    },
    rowsByStudent,
  };
};
